import { css } from '@emotion/react';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Text from '@polestar/component-warehouse-react/components/text/Text';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import styled from '@emotion/styled';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { createCSSTransition, MotionDurations, MotionEasings, Transitions, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Heading from '@polestar/component-warehouse-react/components/heading/Heading';
import Expandable from '@polestar/component-warehouse-react/components/expandable/Expandable';
import React, { forwardRef, Fragment, memo, useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { AccordionContext, AccordionVariants } from './Accordion';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useScrollTo from '@polestar/component-warehouse-react/hooks/useScrollTo';
import { isSSR } from '@polestar/component-warehouse-react/helpers/supports';
import { m as motion, useMotionValue } from 'framer-motion';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { usePrint } from '@polestar/component-warehouse-react/contexts/print';
const AccordionSection = forwardRef(({ children, expanded: _expanded = false, disabled = false, border = true, optimizeRendering = true, largeIcon, id, childOnToggle, onToggle, onToggleEnded, title, subtitle, variant, focusProps, scrollIntoView = false, scrollIntoViewOffset = 0, ...props }, ref) => {
    const internalRef = useRef();
    const combinedRef = useCombinedRef(ref, internalRef);
    const iconRotation = useMotionValue(0);
    const memoizedId = useSSRSafeId('accordion-section', id);
    const { id: contextId, controls: contextControls } = useContext(AccordionContext);
    useEffect(() => {
        contextControls === null || contextControls === void 0 ? void 0 : contextControls.registerSection(contextId, memoizedId);
        return () => {
            contextControls === null || contextControls === void 0 ? void 0 : contextControls.unregisterSection(contextId, memoizedId);
        };
    }, [contextControls, contextId, memoizedId]);
    // We have to listen to changes in the state to go around the memo
    // If we would use normale `useState` for the Accordion it would rerender every section even if there aren't any changes
    const [expanded, setExpanded] = useState(_expanded);
    useEffect(() => contextControls === null || contextControls === void 0 ? void 0 : contextControls.state.on('change', (latestState) => setExpanded(latestState[memoizedId])), [contextControls === null || contextControls === void 0 ? void 0 : contextControls.state, memoizedId]);
    // Sync outside state with internal state
    useEffect(() => {
        setExpanded(_expanded);
    }, [_expanded]);
    const titleId = `${memoizedId}-title`;
    const expandableId = `${memoizedId}-expandable`;
    const { printing } = usePrint();
    const open = printing ? true : expanded && !disabled;
    /**
     * Click handler
     */
    const onTitleClick = useCallback(() => {
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(memoizedId, !expanded);
        childOnToggle === null || childOnToggle === void 0 ? void 0 : childOnToggle(memoizedId, !expanded);
    }, [onToggle, memoizedId, expanded, childOnToggle]);
    const { motion, reset } = useScrollTo(0, isSSR ? null : document.documentElement || document.body, 'scrollTop');
    const handleToggleEnded = useCallback(() => {
        onToggleEnded === null || onToggleEnded === void 0 ? void 0 : onToggleEnded(memoizedId);
    }, [onToggleEnded, memoizedId]);
    const handleAnimationComplete = useCallback(() => {
        var _a;
        const currentScrollTop = (document.documentElement || document.body).scrollTop;
        const value = (((_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) || 0) + currentScrollTop;
        // start from the current position
        reset(currentScrollTop);
        // to
        motion === null || motion === void 0 ? void 0 : motion.set(value - Math.abs(scrollIntoViewOffset), false);
        handleToggleEnded();
    }, [motion, reset, scrollIntoViewOffset, handleToggleEnded]);
    const mouseEnterHandler = useCallback(() => {
        iconRotation.set(iconRotation.get() + 90);
    }, [iconRotation]);
    const showSubtitle = (variant === AccordionVariants.default ||
        (variant === AccordionVariants.titleOnOpen && !open)) &&
        subtitle;
    const showLargeType = variant === AccordionVariants.titleOnOpen;
    const showLabelAsTitle = variant === AccordionVariants.titleOnOpen && !open ? false : showLargeType;
    return (React.createElement(Container, { ...props, ref: combinedRef, disabled: disabled, onMouseEnter: mouseEnterHandler, border: border, id: memoizedId, as: "li" },
        React.createElement(TitleWrapper, { ...focusProps, onClick: onTitleClick, disabled: disabled, expanded: expanded, type: "button", ...aria({
                disabled,
                controls: memoizedId,
                expanded: open,
            }) },
            React.createElement(TitleContainer, { id: titleId },
                typeof title === 'string' ? (showLabelAsTitle ? (React.createElement(StyledTitle, null, title)) : (React.createElement(Title, null, title))) : (title),
                showSubtitle ? React.createElement(SubTitle, { disabled: disabled }, subtitle) : null),
            React.createElement(Spacer, { orientation: Orientations.horizontal, spacing: Spacings.large }),
            React.createElement(IconContainer, { size: showLargeType || largeIcon ? Spacings.xLarge : Spacings.large },
                !printing && (React.createElement(PlusIcon, { initial: false, style: { rotateZ: iconRotation }, animate: { opacity: open ? 0 : 1 }, icon: Icons.plus, pairedWithText: false, size: showLargeType || largeIcon ? IconSizes.medium : IconSizes.small, topOffset: false })),
                printing ? (React.createElement(Icon, { icon: Icons.minus, pairedWithText: false, size: showLargeType || largeIcon ? IconSizes.medium : IconSizes.small, topOffset: false })) : (React.createElement(MinusIcon, { initial: false, animate: { opacity: open ? 1 : 0 }, transition: Transitions.default, icon: Icons.minus, pairedWithText: false, size: showLargeType || largeIcon ? IconSizes.medium : IconSizes.small, topOffset: false })))),
        React.createElement(Expandable, { id: expandableId, renderChildrenOnlyWhenOpen: optimizeRendering, open: open, ...aria({
                labelledby: titleId,
            }), onAnimationComplete: expanded && scrollIntoView ? handleAnimationComplete : handleToggleEnded },
            React.createElement(Fragment, null,
                children,
                React.createElement(Spacer, { spacing: Spacings.large })))));
});
AccordionSection.displayName = 'AccordionSection';
export default memo(AccordionSection);
const Container = styled('div', shouldNotForwardProps(true, 'disabled', 'borderPosition'))(({ theme, disabled, border }) => {
    const { enabled, disabled: disabledState } = theme.accordion.states;
    return css(css `
      position: relative;
      transition:
        ${createCSSTransition('color', MotionEasings.ease, MotionDurations.normal)},
        ${createCSSTransition('border', MotionEasings.ease, MotionDurations.normal)};
    `, disabled &&
        css `
        pointer-events: none;
      `, border &&
        css `
        &:before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: ${createCustomSpacing(1 / 16)};
          background: ${disabled ? disabledState.borderColor : enabled.borderColor};
        }
      `);
});
const TitleWrapper = styled('button', shouldNotForwardProps(true, 'expanded', 'disabled'))(({ theme, disabled, expanded }) => {
    const { enabled, hover, active, disabled: disabledState } = theme.accordion.states;
    return css(css `
      background: none;
      border: none;
      appearance: none;
      text-align: start;
      width: 100%;
      display: flex;
      align-items: center;
      user-select: none;
      justify-content: space-between;
      color: ${enabled.color};
      ${responsiveStyles({
        padding: theme.accordion.padding,
    })};
      -webkit-tap-highlight-color: transparent;
    `, !disabled &&
        expanded &&
        css `
        color: ${active.color};
      `, !disabled &&
        css `
        cursor: pointer;

        &:hover {
          color: ${hover.color};
        }
      `, disabled &&
        css `
        color: ${disabledState.color};
      `);
});
const TitleContainer = styled.div `
  flex-direction: column;
  flex: 1;
`;
const Title = styled(Text) `
  color: inherit;
  display: block;
  flex-grow: 1;
  /* TODO: Tokenize this max-width value; */
  max-width: 832px;
  pointer-events: none;
`;
const StyledTitle = styled(Heading) `
  color: inherit;
  pointer-events: none;
`;
const IconContainer = styled.div(({ size }) => css `
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: ${Spacings.large};
    height: ${size};
    flex-shrink: 0;
  `);
/**
 * TODO: use default button but the problem is that there are rendering issues while rotating
 *
 * Even when positioning the icon at the right position the rotation shows aliasing issues
 * but this approach not.
 *
 * The bottom transition determines the animation speed, not Framer Motion
 */
const PlusIcon = styled(motion(Icon)) `
  position: absolute;
  transition: ${createCSSTransition('transform', MotionEasings.default, 600)};
`;
const MinusIcon = styled(PlusIcon) ``;
const SubTitle = styled(Text)(({ theme, disabled }) => css(css `
      color: ${theme.label.color};
    `, disabled &&
    css `
        color: ${theme.accordion.states.disabled.color};
      `));
